/**
 * CR098/CR229: Deal Cards Tab - Unified Visual Control Panel
 * 
 * Single editor for all deal card configuration:
 * - Card styling (corners, background, shadows, header, decorations)
 * - Ribbon settings (stored in deal_type_registry)
 * - Header composition (stored in template_json.header)
 * - Section visibility + ordering (stored in template_json)
 * - Label overrides (stored in template_json.labels)
 * 
 * CR229 UPDATES:
 * - Replaced old left-rail with 3-zone layout (TopBar + EditorTabs + Preview/Inspector)
 * - Added Data Inspector for field presence + source path transparency
 * - Added MissingTemplateBanner for fallback scenarios
 * - Added TemplateWarningsPanel for soft validation
 * - Integrated SampleDealPicker for real deal preview
 * 
 * AC3-AC6 Compliance:
 * - Draft persistence across refresh via localStorage
 * - Per-deal-type isolation
 * - Discard resets to last saved
 * - Save clears drafts and invalidates provider cache
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { supabase } from "@/integrations/supabase/client";
import { previewConfigStore } from "@/stores/previewConfigStore";
import { toast } from "sonner";
import { 
  CreditCard, 
  Palette, 
  Eye,
  Save,
  RefreshCw,
  AlertCircle,
  Undo2,
  Trash2,
  Bug,
  Puzzle,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import type { SaveTrace } from "@/lib/diagnostics/saveTrace";
import type { 
  DealTypeRegistryEntry, 
  DealCardTemplate, 
  CardTemplateJson,
  CardTemplateSections,
} from "@/providers/DealCardConfigProvider";
import { RIBBON_THEME_CLASSES, useDealCardConfig } from "@/providers/DealCardConfigProvider";
import { DealCardPreview } from "./DealCardPreview";
import { RibbonSettingsPanel, type RibbonDraft } from "./RibbonSettingsPanel";
import { HeaderCompositionPanel, type HeaderComposition, DEFAULT_HEADER_COMPOSITION } from "./HeaderCompositionPanel";
import { SectionTogglesPanel, type ExtendedSectionToggles, DEFAULT_EXTENDED_SECTIONS } from "./SectionTogglesPanel";
import { CardStylingPanel, type CardStyling, DEFAULT_CARD_STYLING, THEME_PRESETS } from "./CardStylingPanel";
import { SectionOrderPanel, DEFAULT_SECTION_ORDER } from "./SectionOrderPanel";
import { useDealCardDrafts, clearAllLocalDrafts, type DealTypeDraft } from "@/hooks/useDealCardDrafts";
import { useNamespacedDrafts, clearAllNamespacedDrafts } from "@/hooks/useNamespacedDrafts";
// CR339: Data Elements editor core
import { ConfigureDataElementsCore } from "./ConfigureDataElementsCore";
// CR412: Deal Card View tab (published truth)
import { DealCardViewTab } from "./DealCardViewTab";
// CR412: Published config hook for publishing to CR412 tables
import { usePublishedDealCardConfig, type DealCardPublishedConfigJson } from "@/hooks/usePublishedDealCardConfig";
import type { PublishEnvironment } from "@/hooks/usePromoteDealCardConfig";
import { setLastPreviewAction } from "@/components/admin/preview";
import { FoldLineSettingsPanel } from "./FoldLineSettingsPanel";
import { CompareSizesPreview } from "./CompareSizesPreview";
import { useFoldLineSettings } from "@/hooks/useFoldLineSettings";
import { DealCardLivePreview } from "./DealCardLivePreview";
import { CR225DebugPanel, type CR225DebugSnapshot } from "@/components/debug/CR225DebugPanel";
import { VisibilitySelfTest } from "@/components/debug/VisibilitySelfTest";
import { type VisibilityInputs } from "@/lib/deals/effectiveVisibility";
import { useLayoutPreviewDeals } from "@/hooks/useLayoutPreviewDeals";
import { useSearchParams } from "react-router-dom";
import { ConfigDebugBar } from "./ConfigDebugBar";
import { useStablePreviewKey } from "@/hooks/useStablePreviewKey";

// CR229: New editor core component
import { DealCardEditorCore } from "./DealCardEditorCore";
// CR402: Components tab view (lifted from Template Editor)
import { ComponentsTabView } from "./ComponentsTabView";
import type { Json } from "@/integrations/supabase/types";
import { getDefaultTemplateForType } from "@/lib/deals/templateResolver";
import { sanitizeDealCardJsonForV2 } from "@/lib/deal-cards/specV2/themeOwnedGuard";
import { clearSpecCacheForType } from "@/lib/deal-cards/specV2/loadSpec";
import { safeUpper } from "@/lib/safeString";
// CR319: Promo badges and details content config types
import {
  type PromoBadgesConfig,
  DEFAULT_PROMO_BADGES_CONFIG,
  normalizePromoBadgesConfig,
} from "@/components/admin/deal-cards/PromoBadgesAccordion";
import {
  type DetailsContentConfig,
  DEFAULT_DETAILS_CONTENT_CONFIG,
  normalizeDetailsContentConfig,
} from "@/components/admin/deal-cards/DetailsContentAccordion";
// CR320: Stable comparison utilities
import { stableEquals } from "@/lib/utils/stableCompare";

// ================================================================
// TYPES
// ================================================================

interface DealCardsTabProps {
  onRefresh?: () => void;
}

// Extended template JSON with all new properties
export interface ExtendedCardTemplateJson extends CardTemplateJson {
  header?: HeaderComposition;
  extended_sections?: Partial<ExtendedSectionToggles>;
  styling?: CardStyling;
  section_order?: string[];
  theme_preset?: "light" | "dark" | "custom";
  // CR323: V2 surface overrides for details block toggles
  surfaceOverrides?: {
    primary?: { blockToggles?: Record<string, boolean>; blockOrder?: string[] };
    details?: { blockToggles?: Record<string, boolean>; blockOrder?: string[] };
  };
  // CR317: Components config for typography/styling of individual components
  components?: ComponentsConfig;
  // CR319: Promo badges config
  promo_badges?: PromoBadgesConfig;
  // CR319: Details content config
  details_content?: DetailsContentConfig;
}

// CR317: Components configuration type
// Stores typography and sub-element config for Header, Ribbon, Starburst, etc.
export interface ComponentTypography {
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: number;
}

// CR319: Re-export promo badges and details content types for external use
export type { PromoBadgesConfig, DetailsContentConfig };

export interface ComponentConfig {
  enabled?: boolean;
  typography?: ComponentTypography;
}

// CR316: Extended header back config with sub-elements
export interface HeaderBackConfig {
  typography?: ComponentTypography;
  merchantLogo?: ComponentConfig;
  merchantName?: ComponentConfig;
  detailsLine?: ComponentConfig;
}
// CR316: Extended starburst config with value and name sub-elements
// CR308: Added assetId for Asset Studio starburst selection
export interface StarburstConfig {
  typography?: ComponentTypography;
  value?: ComponentConfig;
  name?: ComponentConfig;
  /** CR308: Asset Studio starburst ID (null = use theme default or system default) */
  assetId?: string | null;
}

// CR309: Watermark configuration
export type WatermarkPlacement = "center" | "top-left" | "top-right" | "bottom-left" | "bottom-right";
export type WatermarkScaleMode = "fit" | "fill";

export interface WatermarkConfig {
  /** Whether watermark is enabled */
  enabled?: boolean;
  /** Whether front and back use the same asset */
  link_faces?: boolean;
  /** Asset ID for front face (or both if linked) */
  front_asset_id?: string | null;
  /** Asset ID for back face */
  back_asset_id?: string | null;
  /** Opacity (0-0.20, default 0.08) */
  opacity?: number;
  /** Placement on card */
  placement?: WatermarkPlacement;
  /** Scale mode */
  scale_mode?: WatermarkScaleMode;
}

export interface FeedbackConfig {
  /** Label shown in neutral/default state */
  pendingLabel?: string;
  /** Label shown when thumbs up is selected */
  positiveLabel?: string;
  /** Label shown when thumbs down is selected */
  negativeLabel?: string;
}

// CR323: Import RibbonComposerConfig for new ribbon schema
import type { RibbonComposerConfig } from "@/components/admin/deal-cards/RibbonComposer";

export interface ComponentsConfig {
  header_front?: {
    typography?: ComponentTypography;
  };
  header_back?: HeaderBackConfig;
  starburst?: StarburstConfig;
  // CR309: Watermark config
  watermark?: WatermarkConfig;
  // CR310/CR323: Ribbon with graphic selection
  // Legacy fields maintained for backward compatibility
  ribbon?: RibbonComposerConfig | {
    value?: ComponentConfig;
    dealType?: ComponentConfig;
    merchantBrand?: ComponentConfig;
    merchantLogo?: ComponentConfig;
    position?: "top-left" | "top-right";
    offsetX?: number;
    offsetY?: number;
    /** CR310: Graphic source - "system" uses CSS, "asset" uses Asset Studio */
    graphic_source?: "system" | "asset";
    /** CR310: Asset ID when graphic_source is "asset" */
    asset_id?: string | null;
  };
  feedback?: FeedbackConfig;
}

const RIBBON_THEMES = [
  { value: "blue", label: "Blue" },
  { value: "orange", label: "Orange" },
  { value: "purple", label: "Purple" },
  { value: "green", label: "Green" },
  { value: "gray", label: "Gray" },
  { value: "navy", label: "Navy" },
  { value: "red", label: "Red" },
  { value: "amber", label: "Amber" },
  { value: "emerald", label: "Emerald" },
  { value: "teal", label: "Teal" },
];

// ================================================================
// HELPERS: Extract saved config from DB data
// ================================================================

function extractSavedRibbon(regEntry: DealTypeRegistryEntry | undefined, dealTypeKey?: string): RibbonDraft {
  if (!regEntry) {
    // CR326: Fallback derives from the deal type itself, never from BONUS
    return { ribbon_text: dealTypeKey || "DEAL", ribbon_theme: "gray", is_enabled: true };
  }
  return {
    ribbon_text: regEntry.ribbon_text,
    ribbon_theme: regEntry.ribbon_theme,
    is_enabled: regEntry.is_enabled,
  };
}

// CR323: Default details block toggles (used when DB has no overrides)
const DEFAULT_DETAILS_BLOCK_TOGGLES: Record<string, boolean> = {
  back_header: true,
  back_nav: true,
  terms: true,
  fine_print: true,
  source: true,
  evidence: true,
  validator: true,
  details_actions: true,
  eligibility: true,
};

// CR323: Extract surface block toggles from overrides_json (V2 canonical) or template_json.surfaceOverrides (legacy)
function extractSurfaceBlockToggles(template: DealCardTemplate | undefined): {
  primary: Record<string, boolean>;
  details: Record<string, boolean>;
} {
  // First check overrides_json (V2 canonical source)
  const overridesJson = (template as unknown as { overrides_json?: Record<string, unknown> })?.overrides_json;
  if (overridesJson?.surfaceOverrides) {
    const so = overridesJson.surfaceOverrides as {
      primary?: { blockToggles?: Record<string, boolean> };
      details?: { blockToggles?: Record<string, boolean> };
    };
    return {
      primary: so.primary?.blockToggles || {},
      details: { ...DEFAULT_DETAILS_BLOCK_TOGGLES, ...(so.details?.blockToggles || {}) },
    };
  }
  
  // Fallback to template_json.surfaceOverrides (legacy)
  const templateJson = template?.template_json as ExtendedCardTemplateJson | undefined;
  if (templateJson?.surfaceOverrides) {
    return {
      primary: templateJson.surfaceOverrides.primary?.blockToggles || {},
      details: { ...DEFAULT_DETAILS_BLOCK_TOGGLES, ...(templateJson.surfaceOverrides.details?.blockToggles || {}) },
    };
  }
  
  // No overrides - return defaults (all enabled)
  return {
    primary: {},
    details: { ...DEFAULT_DETAILS_BLOCK_TOGGLES },
  };
}

// CR312: Extract surface block order from overrides_json (V2 canonical) or template_json.surfaceOverrides (legacy)
function extractSurfaceBlockOrder(template: DealCardTemplate | undefined): {
  primary: string[];
  details: string[];
} {
  // First check overrides_json (V2 canonical source)
  const overridesJson = (template as unknown as { overrides_json?: Record<string, unknown> })?.overrides_json;
  if (overridesJson?.surfaceOverrides) {
    const so = overridesJson.surfaceOverrides as {
      primary?: { blockOrder?: string[] };
      details?: { blockOrder?: string[] };
    };
    return {
      primary: so.primary?.blockOrder || [],
      details: so.details?.blockOrder || [],
    };
  }
  
  // Fallback to template_json.surfaceOverrides (legacy)
  const templateJson = template?.template_json as ExtendedCardTemplateJson | undefined;
  if (templateJson?.surfaceOverrides) {
    return {
      primary: templateJson.surfaceOverrides.primary?.blockOrder || [],
      details: templateJson.surfaceOverrides.details?.blockOrder || [],
    };
  }
  
  // No order - return empty arrays (use default order)
  return { primary: [], details: [] };
}

// CR326 Phase 2: extractSavedTemplate returns ONLY structural/layout fields.
// Component-owned fields (components, promoBadges, detailsContent) come from deal_card_components_config.
function extractSavedTemplate(template: DealCardTemplate | undefined): {
  header: HeaderComposition;
  sections: ExtendedSectionToggles;
  labels: CardTemplateJson["labels"];
  styling: CardStyling;
  sectionOrder: string[];
  themePreset: "light" | "dark" | "custom";
  surfaceBlockToggles: { primary: Record<string, boolean>; details: Record<string, boolean> };
  surfaceBlockOrder: { primary: string[]; details: string[] };
  promoBadges: PromoBadgesConfig;
  detailsContent: DetailsContentConfig;
} {
  const templateJson = template?.template_json as ExtendedCardTemplateJson | undefined;
  const surfaceBlockToggles = extractSurfaceBlockToggles(template);
  const surfaceBlockOrder = extractSurfaceBlockOrder(template);
  
  if (!templateJson) {
    return {
      header: DEFAULT_HEADER_COMPOSITION,
      sections: DEFAULT_EXTENDED_SECTIONS,
      labels: {},
      styling: DEFAULT_CARD_STYLING,
      sectionOrder: [...DEFAULT_SECTION_ORDER],
      themePreset: "light",
      surfaceBlockToggles,
      surfaceBlockOrder,
      promoBadges: normalizePromoBadgesConfig(undefined),
      detailsContent: normalizeDetailsContentConfig(undefined),
    };
  }
  
  return {
    header: { ...DEFAULT_HEADER_COMPOSITION, ...templateJson.header },
    sections: { ...DEFAULT_EXTENDED_SECTIONS, ...templateJson.sections, ...templateJson.extended_sections },
    labels: templateJson.labels || {},
    styling: { ...DEFAULT_CARD_STYLING, ...templateJson.styling },
    sectionOrder: templateJson.section_order || [...DEFAULT_SECTION_ORDER],
    themePreset: templateJson.theme_preset || "light",
    surfaceBlockToggles,
    surfaceBlockOrder,
    promoBadges: normalizePromoBadgesConfig((templateJson as ExtendedCardTemplateJson | undefined)?.promo_badges),
    detailsContent: normalizeDetailsContentConfig((templateJson as ExtendedCardTemplateJson | undefined)?.details_content),
  };
}

// ================================================================
// COMPONENT
// ================================================================

// CR326: Components config record from deal_card_components_config table
interface ComponentsConfigRecord {
  deal_type_key: string;
  variant_key: string;
  components_json: {
    ribbon?: RibbonDraft;
    components?: ComponentsConfig;

    // Legacy fallback only (CR339: promo/details now live in template_json)
    promoBadges?: PromoBadgesConfig;
    detailsContent?: DetailsContentConfig;
  };
}

export function DealCardsTab({ onRefresh }: DealCardsTabProps) {
  const [registry, setRegistry] = useState<DealTypeRegistryEntry[]>([]);
  const [templates, setTemplates] = useState<DealCardTemplate[]>([]);
  // CR326: Components config from the new dedicated table
  const [componentsConfigs, setComponentsConfigs] = useState<ComponentsConfigRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // CR225: Check for debug mode
  const [searchParams] = useSearchParams();
  const showDebug = searchParams.get("debug") === "1";
  
  // CR225: Use live preview hook for debug panel data
  const {
    deals: livePreviewDeals,
    meta: livePreviewMeta,
    source: livePreviewSource,
    batchRunId: livePreviewBatchId,
  } = useLayoutPreviewDeals(50);
  
  // Provider for cache invalidation
  const { refetch: refetchProvider } = useDealCardConfig();
  
  // CR154: Fold line settings for compare mode
  const { settings: foldLineSettings } = useFoldLineSettings();
  
  // Selected deal type for template editing
  const [selectedType, setSelectedType] = useState<string>("BONUS");
  
  // CR413: Shared environment state (default staging; others disabled in Phase 1)
  const [selectedEnvironment, setSelectedEnvironment] = useState<PublishEnvironment>("staging");
  
  // CR339/CR412: Top-level tab state: view | data | components
  const [topTab, setTopTab] = useState<"view" | "data" | "components">("view");
  
  // CR412: Published config hook for CR412 publish pipeline
  const {
    config: publishedConfig,
    loading: publishedLoading,
    error: publishedError,
    fetchPublished: fetchPublishedConfig,
    publish: publishConfig,
    clearError: clearPublishError,
  } = usePublishedDealCardConfig();
  
  // CR412: Auto-refresh counter — bumped after publish to trigger DealCardViewTab refetch
  const [publishGeneration, setPublishGeneration] = useState(0);
  
  // CR412/CR413: Fetch published config for selected environment + deal type
  useEffect(() => {
    fetchPublishedConfig(selectedEnvironment, selectedType);
  }, [fetchPublishedConfig, selectedEnvironment, selectedType, publishGeneration]);
  
  // Legacy: Registry edits for the table view
  const [registryEdits, setRegistryEdits] = useState<Record<string, Partial<DealTypeRegistryEntry>>>({});
  const [savingRegistry, setSavingRegistry] = useState<string | null>(null);
  
  // ================================================================
  // DRAFT PERSISTENCE (AC3, AC6)
  // ================================================================
  
  const { getDraft, setDraft, discardDraft, hasDraft } = useDealCardDrafts();
  
  // Track if we're initializing from saved vs draft
  const isInitializing = useRef(true);
  
  // CR325: Prevent re-restoring drafts for a deal type already restored this session
  const restoredTypesRef = useRef<Set<string>>(new Set());
  
  // CR111D: Track last action for diagnostics
  const lastActionRef = useRef<string>("initial load");
  
  // CR342: Guard to prevent draft persistence effect from re-creating drafts during save/fetch cycle
  const isSavingRef = useRef(false);
  
  // ================================================================
  // WORKING STATE (current editor values)
  // ================================================================
  
  // CR326: Initial ribbon defaults use gray/generic to prevent BONUS bleed
  // These are immediately overwritten by the hydration effect once data loads
  const [draftRibbon, setDraftRibbon] = useState<RibbonDraft>({
    ribbon_text: "DEAL",
    ribbon_theme: "gray",
    is_enabled: true,
  });
  const [draftHeader, setDraftHeader] = useState<HeaderComposition>(DEFAULT_HEADER_COMPOSITION);
  const [draftSections, setDraftSections] = useState<ExtendedSectionToggles>(DEFAULT_EXTENDED_SECTIONS);
  const [draftLabels, setDraftLabels] = useState<CardTemplateJson["labels"]>({});
  const [draftStyling, setDraftStyling] = useState<CardStyling>(DEFAULT_CARD_STYLING);
  const [draftSectionOrder, setDraftSectionOrder] = useState<string[]>([...DEFAULT_SECTION_ORDER]);
  const [themePreset, setThemePreset] = useState<"light" | "dark" | "custom">("light");
  // CR323: V2 surface-specific block toggles
  const [draftSurfaceBlockToggles, setDraftSurfaceBlockToggles] = useState<{
    primary: Record<string, boolean>;
    details: Record<string, boolean>;
  }>({ primary: {}, details: {} });
  // CR312: V2 surface-specific block order for reordering
  const [draftSurfaceBlockOrder, setDraftSurfaceBlockOrder] = useState<{
    primary: string[];
    details: string[];
  }>({ primary: [], details: [] });
  // CR317: Components config (typography/styling for Header, Ribbon, Starburst, etc.)
  const [draftComponents, setDraftComponents] = useState<ComponentsConfig>({});
  // CR319: Promo badges config
  const [draftPromoBadges, setDraftPromoBadges] = useState<PromoBadgesConfig>({ ...DEFAULT_PROMO_BADGES_CONFIG });
  // CR319: Details content config
  const [draftDetailsContent, setDraftDetailsContent] = useState<DetailsContentConfig>({ ...DEFAULT_DETAILS_CONTENT_CONFIG });
  
  // ================================================================
  // SAVED STATE (last published values from DB)
  // ================================================================
  
  const [savedRibbon, setSavedRibbon] = useState<RibbonDraft | null>(null);
  // CR326 Phase 2: savedTemplate is ONLY structural (no component fields)
  const [savedTemplate, setSavedTemplate] = useState<{
    header: HeaderComposition;
    sections: ExtendedSectionToggles;
    labels: CardTemplateJson["labels"];
    styling: CardStyling;
    sectionOrder: string[];
    themePreset: "light" | "dark" | "custom";
    surfaceBlockToggles: { primary: Record<string, boolean>; details: Record<string, boolean> };
    surfaceBlockOrder: { primary: string[]; details: string[] };
    promoBadges: PromoBadgesConfig;
    detailsContent: DetailsContentConfig;
  } | null>(null);
  // CR326 Phase 2 + CR327: savedComponents is from deal_card_components_config (separate state)
  // Ribbon is now part of components state (components_config is source of truth)
  const [savedComponentsData, setSavedComponentsData] = useState<{
    ribbon: RibbonDraft;
    components: ComponentsConfig;
  } | null>(null);

  // ================================================================
  // DATA FETCHING
  // ================================================================

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("command-center-deal-cards", {
        method: "GET",
      });
      
      if (error) {
        console.error("[DealCardsTab] Fetch error:", error);
        toast.error("Failed to load deal card config");
        return;
      }
      
      setRegistry(data.registry || []);
      setTemplates(data.templates || []);
      // CR326: Store components configs from the new table
      setComponentsConfigs(data.components_config || []);
      setRegistryEdits({});
    } catch (err) {
      console.error("[DealCardsTab] Unexpected error:", err);
      toast.error("Failed to load deal card config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ================================================================
  // CR229: TEMPLATES BY DEAL TYPE KEY (computed map for resolution)
  // ================================================================
  
  const templatesByDealTypeKey = useMemo(() => {
    const map = new Map<string, CardTemplateJson>();
    for (const t of templates) {
      map.set(t.deal_type_key, t.template_json);
    }
    return map;
  }, [templates]);

  // ================================================================
  // CR229: CREATE/DUPLICATE TEMPLATE HANDLERS
  // ================================================================
  
  const handleCreateFromDefault = useCallback(async (dealTypeKey: string): Promise<boolean> => {
    try {
      const defaultTemplate = getDefaultTemplateForType(dealTypeKey);
      // CR315: Apply write-guard sanitizer
      const sanitizedTemplate = sanitizeDealCardJsonForV2(defaultTemplate);
      
      const { error: insertError } = await supabase
        .from("deal_card_templates")
        .upsert([{
          deal_type_key: safeUpper(dealTypeKey) || "BONUS",
          template_json: JSON.parse(JSON.stringify(sanitizedTemplate)) as Json,
          updated_at: new Date().toISOString(),
        }], {
          onConflict: "deal_type_key",
        });
      
      if (insertError) {
        console.error("[handleCreateFromDefault] Insert error:", insertError);
        toast.error("Failed to create template");
        return false;
      }
      
      toast.success(`Created template for ${dealTypeKey}`);
      await fetchData();
      return true;
    } catch (err) {
      console.error("[handleCreateFromDefault] Error:", err);
      toast.error("Failed to create template");
      return false;
    }
  }, [fetchData]);
  
  const handleDuplicateFrom = useCallback(async (
    sourceDealTypeKey: string, 
    targetDealTypeKey: string
  ): Promise<boolean> => {
    try {
      const sourceTemplate = templatesByDealTypeKey.get(safeUpper(sourceDealTypeKey) || "BONUS");
      if (!sourceTemplate) {
        toast.error(`Source template not found: ${sourceDealTypeKey}`);
        return false;
      }
      
      // CR315: Apply write-guard sanitizer
      const sanitizedTemplate = sanitizeDealCardJsonForV2(sourceTemplate);
      
      const { error: insertError } = await supabase
        .from("deal_card_templates")
        .upsert([{
          deal_type_key: safeUpper(targetDealTypeKey) || "BONUS",
          template_json: JSON.parse(JSON.stringify(sanitizedTemplate)) as Json,
          updated_at: new Date().toISOString(),
        }], {
          onConflict: "deal_type_key",
        });
      
      if (insertError) {
        console.error("[handleDuplicateFrom] Insert error:", insertError);
        toast.error("Failed to duplicate template");
        return false;
      }
      
      toast.success(`Duplicated template from ${sourceDealTypeKey} to ${targetDealTypeKey}`);
      await fetchData();
      return true;
    } catch (err) {
      console.error("[handleDuplicateFrom] Error:", err);
      toast.error("Failed to duplicate template");
      return false;
    }
  }, [templatesByDealTypeKey, fetchData]);

  // ================================================================
  // SYNC WORKING STATE WHEN SELECTED TYPE CHANGES (AC3, AC6)
  // ================================================================
  
  // CR325: Memoize registry/template lookups to avoid effect re-runs from array identity changes
  const regEntry = useMemo(() => registry.find(r => r.deal_type_key === selectedType), [registry, selectedType]);
  const templateEntry = useMemo(() => templates.find(t => t.deal_type_key === selectedType), [templates, selectedType]);
  // CR326: Memoize components config lookup from the new table
  // CR326 Phase 2: MUST match BOTH keys to prevent cross-type bleed
  const componentsConfigEntry = useMemo(() => componentsConfigs.find(
    c => c.deal_type_key === selectedType && c.variant_key === "standard_v2"
  ), [componentsConfigs, selectedType]);

  useEffect(() => {
    if (loading) return; // Wait for data to load
    
    // CR325/CR326: Guard — only restore once per deal type per session
    // After save, the type is removed from the set so re-hydration can happen
    if (restoredTypesRef.current.has(selectedType)) return;
    restoredTypesRef.current.add(selectedType);
    
    isInitializing.current = true;
    
    // CR326: Template saved state (structure/layout only, no component keys)
    const savedTpl = extractSavedTemplate(templateEntry);
    const savedRib = extractSavedRibbon(regEntry, selectedType);
    
    // CR326 Phase 2: Components from deal_card_components_config (authoritative)
    // CR327: Ribbon is now sourced from components_config first, registry fallback
    const compJson = componentsConfigEntry?.components_json;
    const compRibbon = compJson?.ribbon as { ribbon_text?: string; ribbon_theme?: string } | undefined;
    const savedComp = {
      ribbon: (compRibbon?.ribbon_text && compRibbon?.ribbon_theme)
        ? { ribbon_text: compRibbon.ribbon_text, ribbon_theme: compRibbon.ribbon_theme, is_enabled: savedRib.is_enabled }
        : savedRib, // Fallback to registry if components_config ribbon is empty
      components: compJson?.components || {},
    };

    // CR339: Promo/Details are Data Elements (template_json). Legacy fallback reads old components_json if needed.
    const savedPromoBadges = normalizePromoBadgesConfig(
      (templateEntry?.template_json as ExtendedCardTemplateJson | undefined)?.promo_badges ??
      (compJson as unknown as { promoBadges?: PromoBadgesConfig })?.promoBadges
    );

    const savedDetailsContent = normalizeDetailsContentConfig(
      (templateEntry?.template_json as ExtendedCardTemplateJson | undefined)?.details_content ??
      (compJson as unknown as { detailsContent?: DetailsContentConfig })?.detailsContent
    );
    
    // Store saved state for dirty checking
    setSavedRibbon(savedRib);
    setSavedTemplate({
      header: savedTpl.header,
      sections: savedTpl.sections,
      labels: savedTpl.labels,
      styling: savedTpl.styling,
      sectionOrder: savedTpl.sectionOrder,
      themePreset: savedTpl.themePreset,
      surfaceBlockToggles: savedTpl.surfaceBlockToggles,
      surfaceBlockOrder: savedTpl.surfaceBlockOrder,
      promoBadges: savedPromoBadges,
      detailsContent: savedDetailsContent,
    });
    // CR326 Phase 2: Components saved state is separate
    setSavedComponentsData(savedComp);
    
    // Check for persisted draft (AC3 - draft persistence across refresh)
    const existingDraft = getDraft(selectedType);
    
    if (existingDraft) {
      // Restore from persisted draft
      console.log(`[DealCardsTab] Restoring persisted draft for ${selectedType}`);
      setDraftRibbon(existingDraft.ribbon);
      setDraftHeader(existingDraft.header);
      setDraftSections(existingDraft.sections);
      setDraftLabels(existingDraft.labels);
      setDraftStyling(existingDraft.styling);
      setDraftSectionOrder(existingDraft.sectionOrder);
      setThemePreset(existingDraft.themePreset);
      setDraftSurfaceBlockToggles(existingDraft.surfaceBlockToggles || savedTpl.surfaceBlockToggles);
      setDraftSurfaceBlockOrder(existingDraft.surfaceBlockOrder || savedTpl.surfaceBlockOrder);
      setDraftComponents(existingDraft.components || savedComp.components);
      setDraftPromoBadges(existingDraft.promoBadges || savedPromoBadges);
      setDraftDetailsContent(normalizeDetailsContentConfig(existingDraft.detailsContent ?? savedDetailsContent));
    } else {
      // No draft - use saved values
      // CR327: Ribbon from components_config is authoritative
      setDraftRibbon(savedComp.ribbon);
      setDraftHeader(savedTpl.header);
      setDraftSections(savedTpl.sections);
      setDraftLabels(savedTpl.labels);
      setDraftStyling(savedTpl.styling);
      setDraftSectionOrder(savedTpl.sectionOrder);
      setThemePreset(savedTpl.themePreset);
      setDraftSurfaceBlockToggles(savedTpl.surfaceBlockToggles);
      setDraftSurfaceBlockOrder(savedTpl.surfaceBlockOrder);
      setDraftComponents(savedComp.components);
      setDraftPromoBadges(savedPromoBadges);
      setDraftDetailsContent(normalizeDetailsContentConfig(savedDetailsContent));
    }
    
    isInitializing.current = false;
  }, [selectedType, loading, regEntry, templateEntry, componentsConfigEntry]); // CR326: Include componentsConfigEntry for re-baseline

  // ================================================================
  // PERSIST DRAFT TO LOCALSTORAGE ON CHANGES
  // ================================================================
  
  useEffect(() => {
    // CR342: Skip draft persistence during save/fetch cycle to prevent re-creating
    // drafts from stale state while fetchData() is in flight.
    if (isInitializing.current || isSavingRef.current || !savedRibbon || !savedTemplate || !savedComponentsData) return;
    
    // Build current draft (CR323: include surfaceBlockToggles, CR312: include surfaceBlockOrder, CR316: include components, CR319: include promoBadges/detailsContent)
    const currentDraft: DealTypeDraft = {
      ribbon: draftRibbon,
      header: draftHeader,
      sections: draftSections,
      labels: draftLabels,
      styling: draftStyling,
      sectionOrder: draftSectionOrder,
      themePreset,
      surfaceBlockToggles: draftSurfaceBlockToggles,
      surfaceBlockOrder: draftSurfaceBlockOrder,
      components: draftComponents,
      promoBadges: draftPromoBadges,
      detailsContent: draftDetailsContent,
      savedAt: new Date().toISOString(),
    };
    
    // CR320/CR327: Use stable comparison to prevent phantom dirty from key ordering
    // Ribbon compares against savedComponentsData (source of truth)
    const isDirty = 
      !stableEquals(draftRibbon, savedComponentsData.ribbon) ||
      !stableEquals(draftHeader, savedTemplate.header) ||
      !stableEquals(draftSections, savedTemplate.sections) ||
      !stableEquals(draftLabels, savedTemplate.labels) ||
      !stableEquals(draftStyling, savedTemplate.styling) ||
      !stableEquals(draftSectionOrder, savedTemplate.sectionOrder) ||
      !stableEquals(draftSurfaceBlockToggles, savedTemplate.surfaceBlockToggles) ||
      !stableEquals(draftSurfaceBlockOrder, savedTemplate.surfaceBlockOrder) ||
      !stableEquals(draftComponents, savedComponentsData.components) ||
      !stableEquals(draftPromoBadges, savedTemplate.promoBadges) ||
      !stableEquals(draftDetailsContent, savedTemplate.detailsContent) ||
      themePreset !== savedTemplate.themePreset;
    
    if (isDirty) {
      // CR325: Skip no-op writes — only persist if draft content actually changed
      const existingDraft = getDraft(selectedType);
      if (!existingDraft || !stableEquals(currentDraft.ribbon, existingDraft.ribbon) ||
          !stableEquals(currentDraft.header, existingDraft.header) ||
          !stableEquals(currentDraft.sections, existingDraft.sections) ||
          !stableEquals(currentDraft.labels, existingDraft.labels) ||
          !stableEquals(currentDraft.styling, existingDraft.styling) ||
          !stableEquals(currentDraft.sectionOrder, existingDraft.sectionOrder) ||
          !stableEquals(currentDraft.surfaceBlockToggles, existingDraft.surfaceBlockToggles) ||
          !stableEquals(currentDraft.surfaceBlockOrder, existingDraft.surfaceBlockOrder) ||
          !stableEquals(currentDraft.components, existingDraft.components) ||
          !stableEquals(currentDraft.promoBadges, existingDraft.promoBadges) ||
          !stableEquals(currentDraft.detailsContent, existingDraft.detailsContent) ||
          currentDraft.themePreset !== existingDraft.themePreset) {
        setDraft(selectedType, currentDraft);
      }
    } else {
      // No changes - clear any existing draft
      if (getDraft(selectedType)) {
        discardDraft(selectedType);
      }
    }
  }, [
    draftRibbon, draftHeader, draftSections, draftLabels, draftStyling, draftSectionOrder, themePreset,
    draftSurfaceBlockToggles, draftSurfaceBlockOrder, draftComponents, draftPromoBadges, draftDetailsContent,
    savedRibbon, savedTemplate, savedComponentsData, selectedType, setDraft, discardDraft, getDraft
  ]);

  // CR173/CR313: Push deal card changes to unified preview store
  // CR313: Include V2 surfaceOverrides, components, promo_badges, details_content for Structure toggles
  useEffect(() => {
    if (isInitializing.current) return;
    
    previewConfigStore.setDealCardOverrides(selectedType, {
      registry: {
        ribbon_text: draftRibbon.ribbon_text,
        ribbon_theme: draftRibbon.ribbon_theme,
        is_enabled: draftRibbon.is_enabled,
      },
      template: {
        sections: draftSections,
        labels: draftLabels,
        header: draftHeader,
        extended_sections: draftSections,
        styling: draftStyling,
        section_order: draftSectionOrder,
        theme_preset: themePreset,
        // CR313: V2 surfaceOverrides - canonical for Structure tab toggles (ribbon, details_cta, etc.)
        surfaceOverrides: {
          primary: {
            blockToggles: draftSurfaceBlockToggles.primary,
            blockOrder: draftSurfaceBlockOrder.primary.length > 0 ? draftSurfaceBlockOrder.primary : undefined,
          },
          details: {
            blockToggles: draftSurfaceBlockToggles.details,
            blockOrder: draftSurfaceBlockOrder.details.length > 0 ? draftSurfaceBlockOrder.details : undefined,
          },
        },
        // CR313: Include components, promo_badges, details_content for accurate preview
        components: draftComponents,
        promo_badges: draftPromoBadges,
        details_content: draftDetailsContent,
      },
    });
  }, [
    selectedType, draftRibbon, draftHeader, draftSections, draftLabels, draftStyling, 
    draftSectionOrder, themePreset, draftSurfaceBlockToggles, draftSurfaceBlockOrder,
    draftComponents, draftPromoBadges, draftDetailsContent
  ]);

  // ================================================================
  // DIRTY STATE TRACKING
  // ================================================================
  
  // ================================================================
  // CR339: SPLIT DIRTY DETECTION — Data Elements vs Components
  // ================================================================
  
  // CR339: Data Elements dirty — structure, toggles, ordering, promoBadges, detailsContent
  const hasUnsavedDataChanges = useMemo(() => {
    if (!savedTemplate) return false;
    return (
      !stableEquals(draftSections, savedTemplate.sections) ||
      !stableEquals(draftSectionOrder, savedTemplate.sectionOrder) ||
      !stableEquals(draftSurfaceBlockToggles, savedTemplate.surfaceBlockToggles) ||
      !stableEquals(draftSurfaceBlockOrder, savedTemplate.surfaceBlockOrder) ||
      !stableEquals(draftPromoBadges, savedTemplate.promoBadges) ||
      !stableEquals(draftDetailsContent, savedTemplate.detailsContent)
    );
  }, [draftSections, draftSectionOrder, draftSurfaceBlockToggles, draftSurfaceBlockOrder, draftPromoBadges, draftDetailsContent, savedTemplate]);
  
  // CR339: Components dirty — ribbon, header, labels, styling, themePreset, components
  const hasUnsavedComponentChanges = useMemo(() => {
    if (!savedTemplate || !savedComponentsData) return false;
    return (
      !stableEquals(draftRibbon, savedComponentsData.ribbon) ||
      !stableEquals(draftHeader, savedTemplate.header) ||
      !stableEquals(draftLabels, savedTemplate.labels) ||
      !stableEquals(draftStyling, savedTemplate.styling) ||
      !stableEquals(draftComponents, savedComponentsData.components) ||
      themePreset !== savedTemplate.themePreset
    );
  }, [draftRibbon, draftHeader, draftLabels, draftStyling, draftComponents, themePreset, savedTemplate, savedComponentsData]);
  
  // Legacy: overall dirty for draft persistence
  const isRibbonDirty = useMemo(() => {
    if (!savedComponentsData) return false;
    return !stableEquals(draftRibbon, savedComponentsData.ribbon);
  }, [draftRibbon, savedComponentsData]);
  const isTemplateDirty = hasUnsavedDataChanges;
  const isComponentsDirty = hasUnsavedComponentChanges;
  
  const hasUnsavedChanges = hasUnsavedDataChanges || hasUnsavedComponentChanges;
  
  // CR226: Use stable preview key with debouncing to prevent jitter
  const draftConfig = useMemo(() => ({
    ribbon: draftRibbon,
    sections: draftSections,
    styling: draftStyling,
    header: draftHeader,
    labels: draftLabels,
    sectionOrder: draftSectionOrder,
    themePreset,
  }), [draftRibbon, draftSections, draftStyling, draftHeader, draftLabels, draftSectionOrder, themePreset]);
  
  const { previewKey, configHash: draftHash, isDebouncing } = useStablePreviewKey(draftConfig, {
    prefix: selectedType,
    debounceMs: 150, // Debounce 150ms to prevent jitter
  });
  
  // CR226: Track last saved timestamp for display
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  
  // CR340: Save trace for debug diagnostics
  const [lastSaveTrace, setLastSaveTrace] = useState<SaveTrace | null>(null);

  // ================================================================
  // DRAFT UPDATE HANDLERS
  // ================================================================
  
  const handleRibbonChange = useCallback((update: Partial<RibbonDraft>) => {
    setDraftRibbon(prev => ({ ...prev, ...update }));
  }, []);
  
  const handleHeaderChange = useCallback((update: Partial<HeaderComposition>) => {
    setDraftHeader(prev => ({ ...prev, ...update }));
  }, []);
  
  const handleSectionToggle = useCallback((section: keyof ExtendedSectionToggles, value: boolean) => {
    setDraftSections(prev => ({ ...prev, [section]: value }));
  }, []);
  
  const handleLabelChange = useCallback((key: string, value: string) => {
    setDraftLabels(prev => ({ ...prev, [key]: value }));
  }, []);
  
  // CR225: Sync styling.showMerchantName to sections.merchant_name for single source of truth
  // CR313: Removed ribbon sync - V2 uses surfaceOverrides.primary.blockToggles.ribbon as SSoT
  const handleStylingChange = useCallback((update: Partial<CardStyling>) => {
    setDraftStyling(prev => ({ ...prev, ...update }));
    
    // CR225: If showMerchantName changed, sync it to sections to maintain storage compatibility
    if ('showMerchantName' in update) {
      setDraftSections(prev => ({ ...prev, merchant_name: update.showMerchantName ?? true }));
    }
    // CR313: DO NOT sync showRibbon to sections - V2 uses blockToggles.ribbon as canonical source
    // Legacy showRibbon in styling is ignored; Structure tab toggle is SSoT
  }, []);
  
  const handleSectionOrderChange = useCallback((newOrder: string[]) => {
    setDraftSectionOrder(newOrder);
  }, []);
  
  const handleThemeChange = useCallback((theme: "light" | "dark" | "custom") => {
    setThemePreset(theme);
  }, []);
  
  // CR323: Handle surface-specific block toggle changes
  const handleSurfaceBlockToggle = useCallback((surface: 'primary' | 'details', blockId: string, enabled: boolean) => {
    setDraftSurfaceBlockToggles(prev => ({
      ...prev,
      [surface]: {
        ...prev[surface],
        [blockId]: enabled,
      },
    }));
  }, []);
  
  // CR312: Handle surface-specific block order changes (for drag/drop reordering)
  const handleSurfaceBlockOrderChange = useCallback((surface: 'primary' | 'details', newOrder: string[]) => {
    setDraftSurfaceBlockOrder(prev => ({
      ...prev,
      [surface]: newOrder,
    }));
  }, []);
  
  // CR316: Handle components config changes (typography, etc.)
  const handleComponentsChange = useCallback((update: Partial<ComponentsConfig>) => {
    setDraftComponents(prev => ({ ...prev, ...update }));
  }, []);
  
  // CR319: Handle promo badges config changes
  const handlePromoBadgesChange = useCallback((config: PromoBadgesConfig) => {
    setDraftPromoBadges(config);
  }, []);
  
  // CR319: Handle details content config changes
  const handleDetailsContentChange = useCallback((config: DetailsContentConfig) => {
    setDraftDetailsContent(config);
  }, []);

  // ================================================================
  // DISCARD CHANGES (AC4 - Reset to last saved version with fresh DB fetch)
  // ================================================================
  
  const discardChanges = useCallback(async () => {
    // 1. Clear persisted draft first
    discardDraft(selectedType);
    
    // 2. Refetch fresh data from DB (not stale cache)
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("command-center-deal-cards", {
        method: "GET",
      });
      
      if (error) {
        console.error("[DealCardsTab] Discard refetch error:", error);
        toast.error("Failed to fetch latest config");
        return;
      }
      
      // Update local state with fresh DB values
      setRegistry(data.registry || []);
      setTemplates(data.templates || []);
      // CR326 Phase 2: Also update components configs
      setComponentsConfigs(data.components_config || []);
      
      // Find fresh saved values
      const regEntry = (data.registry || []).find((r: DealTypeRegistryEntry) => r.deal_type_key === selectedType);
      const template = (data.templates || []).find((t: DealCardTemplate) => t.deal_type_key === selectedType);
      // CR326 Phase 2: Find components config from dedicated table
      const compConfig = (data.components_config || []).find(
        (c: ComponentsConfigRecord) => c.deal_type_key === selectedType && c.variant_key === "standard_v2"
      );
      
      const freshTpl = extractSavedTemplate(template);
      const freshRibbon = extractSavedRibbon(regEntry, selectedType);
      const freshCompJson = compConfig?.components_json;
      const freshCompRibbon = freshCompJson?.ribbon as { ribbon_text?: string; ribbon_theme?: string } | undefined;
      const freshComp = {
        ribbon: (freshCompRibbon?.ribbon_text && freshCompRibbon?.ribbon_theme)
          ? { ribbon_text: freshCompRibbon.ribbon_text, ribbon_theme: freshCompRibbon.ribbon_theme, is_enabled: freshRibbon.is_enabled }
          : freshRibbon,
        components: freshCompJson?.components || {},
      };

      // CR339: promo/details from template_json (legacy fallback to components_json)
      const freshPromoBadges = normalizePromoBadgesConfig(
        (template?.template_json as ExtendedCardTemplateJson | undefined)?.promo_badges ??
        (freshCompJson as unknown as { promoBadges?: PromoBadgesConfig })?.promoBadges
      );

      const freshDetailsContent = normalizeDetailsContentConfig(
        (template?.template_json as ExtendedCardTemplateJson | undefined)?.details_content ??
        (freshCompJson as unknown as { detailsContent?: DetailsContentConfig })?.detailsContent
      );
      
      // 3. Update saved state cache
      setSavedRibbon(freshRibbon);
      setSavedTemplate({
        header: freshTpl.header,
        sections: freshTpl.sections,
        labels: freshTpl.labels,
        styling: freshTpl.styling,
        sectionOrder: freshTpl.sectionOrder,
        themePreset: freshTpl.themePreset,
        surfaceBlockToggles: freshTpl.surfaceBlockToggles,
        surfaceBlockOrder: freshTpl.surfaceBlockOrder,
        promoBadges: freshPromoBadges,
        detailsContent: freshDetailsContent,
      });
      setSavedComponentsData(freshComp);
      
      // 4. Reset working state to fresh DB values
      // CR327: Ribbon from components_config is authoritative
      setDraftRibbon({ ...freshComp.ribbon });
      setDraftHeader({ ...freshTpl.header });
      setDraftSections({ ...freshTpl.sections });
      setDraftLabels({ ...freshTpl.labels });
      setDraftStyling({ ...freshTpl.styling });
      setDraftSectionOrder([...freshTpl.sectionOrder]);
      setThemePreset(freshTpl.themePreset);
      setDraftSurfaceBlockToggles(freshTpl.surfaceBlockToggles);
      setDraftSurfaceBlockOrder(freshTpl.surfaceBlockOrder);
      // CR326 Phase 2: Components from dedicated table
      setDraftComponents(freshComp.components);
      setDraftPromoBadges(freshPromoBadges);
      setDraftDetailsContent(normalizeDetailsContentConfig(freshDetailsContent));
      
      toast.info("Changes discarded - restored to last saved version");
    } catch (err) {
      console.error("[DealCardsTab] Discard error:", err);
      toast.error("Failed to discard changes");
    } finally {
      setLoading(false);
    }
  }, [selectedType, discardDraft]);

  // ================================================================
  // CLEAR ALL LOCAL DRAFTS (Global reset for stuck drafts)
  // ================================================================
  
  const handleClearAllDrafts = useCallback(() => {
    clearAllLocalDrafts();
    
    // Reset current working state to saved values
    if (savedRibbon && savedTemplate) {
      setDraftRibbon({ ...savedRibbon });
      setDraftHeader({ ...savedTemplate.header });
      setDraftSections({ ...savedTemplate.sections });
      setDraftLabels({ ...savedTemplate.labels });
      setDraftStyling({ ...savedTemplate.styling });
      setDraftSectionOrder([...savedTemplate.sectionOrder]);
      setThemePreset(savedTemplate.themePreset);
      setDraftSurfaceBlockToggles(savedTemplate.surfaceBlockToggles);
      setDraftSurfaceBlockOrder(savedTemplate.surfaceBlockOrder);
    }
    // CR326 Phase 2: Restore components from separate saved state
    if (savedComponentsData) {
      // CR327: Ribbon comes from components saved state
      setDraftRibbon(savedComponentsData.ribbon);
      setDraftComponents(savedComponentsData.components);
    }
    // CR339: Promo/details from template saved state
    if (savedTemplate) {
      setDraftPromoBadges(savedTemplate.promoBadges);
      setDraftDetailsContent(normalizeDetailsContentConfig(savedTemplate.detailsContent));
    }
    
    toast.success("All local drafts cleared (DB values unchanged)");
  }, [savedRibbon, savedTemplate, savedComponentsData]);

  // ================================================================
  // CR326: SPLIT SAVE — Registry, Template, Components go to separate stores
  // Only clears drafts AFTER successful server save
  // ================================================================
  
  const saveAll = async () => {
    setSaving(true);
    isSavingRef.current = true; // CR342: Block draft persistence during save
    let registrySaved = false;
    let templateSaved = false;
    let componentsSaved = false;
    
    try {
      // 1. Save registry if dirty
      if (isRibbonDirty) {
        const { error: regError } = await supabase.functions.invoke("command-center-deal-cards", {
          method: "POST",
          body: {
            _action: "update_registry",
            deal_type_key: selectedType,
            ribbon_text: draftRibbon.ribbon_text,
            ribbon_theme: draftRibbon.ribbon_theme,
            is_enabled: draftRibbon.is_enabled,
          },
        });
        
        if (regError) {
          throw new Error(`Registry save failed: ${regError.message}`);
        }
        registrySaved = true;
      }
      
      // 2. Save template if dirty (CR326: strips component-owned keys)
      if (isTemplateDirty) {
        const { ribbon, debug_strip, merchant_category_chips, confidence_indicator, claim_button, ...coreSections } = draftSections;
        
        // CR339: Template payload includes promo_badges + details_content (Data Elements owned)
        const templateJson: ExtendedCardTemplateJson = {
          sections: coreSections as CardTemplateSections,
          labels: draftLabels,
          header: draftHeader,
          extended_sections: {
            ribbon,
            debug_strip,
            merchant_category_chips,
            confidence_indicator,
            claim_button,
          },
          styling: draftStyling,
          section_order: draftSectionOrder,
          theme_preset: themePreset,
          surfaceOverrides: {
            primary: { 
              blockToggles: draftSurfaceBlockToggles.primary,
              blockOrder: draftSurfaceBlockOrder.primary.length > 0 ? draftSurfaceBlockOrder.primary : undefined,
            },
            details: { 
              blockToggles: draftSurfaceBlockToggles.details,
              blockOrder: draftSurfaceBlockOrder.details.length > 0 ? draftSurfaceBlockOrder.details : undefined,
            },
          },
          promo_badges: draftPromoBadges,
          details_content: draftDetailsContent,
        };
        
        console.log("[DealCardsTab] CR326: Template save (no component keys):", {
          deal_type_key: selectedType,
          "blockToggles.ribbon": draftSurfaceBlockToggles.primary?.ribbon,
        });
        
        const { error: tplError } = await supabase.functions.invoke("command-center-deal-cards", {
          method: "POST",
          body: {
            _action: "update_template",
            deal_type_key: selectedType,
            template_json: templateJson,
          },
        });
        
        if (tplError) {
          throw new Error(`Template save failed: ${tplError.message}`);
        }
        templateSaved = true;
      }
      
      // 3. CR326: Save components to deal_card_components_config if dirty
      if (isComponentsDirty) {
        const componentsPayload = {
          ribbon: draftRibbon, // Mirror ribbon draft for components store
          components: draftComponents,
          // CR339: promo_badges + details_content are saved in template_json (Data Elements)
        };
        
        console.log("[DealCardsTab] CR326: Components save to dedicated table:", {
          deal_type_key: selectedType,
          keys: Object.keys(componentsPayload),
        });
        
        const { error: compError } = await supabase.functions.invoke("command-center-deal-cards", {
          method: "POST",
          body: {
            _action: "update_components",
            deal_type_key: selectedType,
            variant_key: "standard_v2",
            components_json: componentsPayload,
          },
        });
        
        if (compError) {
          throw new Error(`Components save failed: ${compError.message}`);
        }
        componentsSaved = true;
      }
      
      // 4. Success feedback
      const parts = [];
      if (registrySaved) parts.push("ribbon");
      if (templateSaved) parts.push("template");
      if (componentsSaved) parts.push("components");
      
      if (parts.length > 0) {
        toast.success(`Saved ${parts.join(" + ")} for ${selectedType}`);
        setLastSavedAt(new Date().toISOString());
      } else {
        toast.info("No changes to save");
        return; // Early exit if nothing saved
      }
      
      // 4. Only clear draft AFTER successful server save (AC5 compliance)
      discardDraft(selectedType);
      
      // 5. CR341: Delete guard BEFORE fetchData so hydration effect re-runs
      restoredTypesRef.current.delete(selectedType);
      
      // 6. Refresh data from DB (updates registry/templates arrays)
      await fetchData();
      
      // 7. Invalidate provider cache (AC5 - all pages pick up new config)
      await refetchProvider();
      
      // 8. Notify parent
      onRefresh?.();
      
    } catch (err) {
      // ON ERROR: Drafts remain persisted, preview stays on draft
      console.error("[DealCardsTab] Save error:", err);
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast.error(`Save failed: ${msg}. Draft preserved.`);
    } finally {
      isSavingRef.current = false; // CR342: Re-enable draft persistence
      setSaving(false);
    }
  };

  // ================================================================
  // LEGACY REGISTRY TABLE HANDLERS
  // ================================================================

  const handleRegistryEdit = (key: string, field: keyof DealTypeRegistryEntry, value: unknown) => {
    setRegistryEdits(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
  };

  const getRegistryValue = <K extends keyof DealTypeRegistryEntry>(
    key: string, 
    field: K
  ): DealTypeRegistryEntry[K] => {
    const edit = registryEdits[key]?.[field];
    if (edit !== undefined) return edit as DealTypeRegistryEntry[K];
    const original = registry.find(r => r.deal_type_key === key);
    return original?.[field] as DealTypeRegistryEntry[K];
  };

  const hasRegistryChanges = (key: string): boolean => {
    return Object.keys(registryEdits[key] || {}).length > 0;
  };

  const saveRegistryEntry = async (dealTypeKey: string) => {
    const edits = registryEdits[dealTypeKey];
    if (!edits || Object.keys(edits).length === 0) return;
    
    setSavingRegistry(dealTypeKey);
    try {
      const { data, error } = await supabase.functions.invoke("command-center-deal-cards", {
        method: "POST",
        body: {
          _action: "update_registry",
          deal_type_key: dealTypeKey,
          ...edits,
        },
      });
      
      if (error) {
        const errorMessage = (data as { message?: string })?.message || error.message || "Unknown error";
        throw new Error(errorMessage);
      }
      
      toast.success(`Saved ${dealTypeKey} registry`);
      setRegistryEdits(prev => {
        const next = { ...prev };
        delete next[dealTypeKey];
        return next;
      });
      fetchData();
      refetchProvider(); // Invalidate cache
      onRefresh?.();
    } catch (err) {
      console.error("[DealCardsTab] Save registry error:", err);
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      toast.error(`Failed to save ${dealTypeKey}: ${errorMessage}`);
    } finally {
      setSavingRegistry(null);
    }
  };

  // ================================================================
  // GET PREVIEW OVERRIDES (always from draft state)
  // ================================================================

  const getPreviewRegistry = (): DealTypeRegistryEntry | undefined => {
    const original = registry.find(r => r.deal_type_key === selectedType);
    if (!original) return undefined;
    
    return {
      ...original,
      ribbon_text: draftRibbon.ribbon_text,
      ribbon_theme: draftRibbon.ribbon_theme,
      is_enabled: draftRibbon.is_enabled,
    };
  };
  
  // CR313: getPreviewTemplate - V2 uses surfaceOverrides.primary.blockToggles as SSoT for ribbon
  // DO NOT use draftStyling.showRibbon for extended_sections.ribbon - that's legacy
  const getPreviewTemplate = (): ExtendedCardTemplateJson => {
    // CR313: Ribbon visibility from V2 blockToggles (SSoT), with fallback
    const ribbonEnabled = draftSurfaceBlockToggles.primary?.ribbon ?? true;
    
    return {
      sections: draftSections as CardTemplateSections,
      labels: draftLabels,
      header: draftHeader,
      extended_sections: {
        ribbon: ribbonEnabled, // CR313: V2 SSoT from blockToggles, NOT styling.showRibbon
        debug_strip: draftSections.debug_strip,
        merchant_category_chips: draftSections.merchant_category_chips,
        merchant_name: draftStyling.showMerchantName, // CR225: Single source from styling
        confidence_indicator: draftSections.confidence_indicator,
        claim_button: draftSections.claim_button,
      },
      styling: draftStyling,
      section_order: draftSectionOrder,
      theme_preset: themePreset,
      // CR313: Include V2 surfaceOverrides for preview parity
      surfaceOverrides: {
        primary: {
          blockToggles: draftSurfaceBlockToggles.primary,
          blockOrder: draftSurfaceBlockOrder.primary.length > 0 ? draftSurfaceBlockOrder.primary : undefined,
        },
        details: {
          blockToggles: draftSurfaceBlockToggles.details,
          blockOrder: draftSurfaceBlockOrder.details.length > 0 ? draftSurfaceBlockOrder.details : undefined,
        },
      },
    };
  };

  // CR339: Compute saved component values for Data Elements preview
  const savedComponentsForPreview = useMemo(() => ({
    ribbon: savedComponentsData?.ribbon ?? { ribbon_text: selectedType, ribbon_theme: "gray", is_enabled: true },
    header: savedTemplate?.header ?? DEFAULT_HEADER_COMPOSITION,
    labels: savedTemplate?.labels ?? {},
    styling: savedTemplate?.styling ?? DEFAULT_CARD_STYLING,
    themePreset: savedTemplate?.themePreset ?? "light" as const,
    components: savedComponentsData?.components ?? {},
  }), [savedComponentsData, savedTemplate, selectedType]);

  // ================================================================
  // CR412: PUBLISH TO CR412 TABLES (combined config_json)
  // Called after legacy save succeeds, so CR412 "truth" stays in sync.
  // ================================================================
  
  /**
   * CR412: Publish a slice to CR412 tables.
   * @param dealType - explicit deal type to publish (avoids stale selectedType closure)
   * @param slice - which slice is being updated
   * @param sliceData - the new slice payload
   * @returns true if publish succeeded, false otherwise
   */
  const publishToCR412 = useCallback(async (
    dealType: string,
    slice: "data_elements" | "components",
    sliceData: Record<string, unknown>,
  ): Promise<boolean> => {
    try {
      console.log("[CR412] publishToCR412 start", { dealType, slice, environment: selectedEnvironment });

      // Re-fetch current published to get latest active_version_id for concurrency
      const current = await fetchPublishedConfig(selectedEnvironment, dealType);
      const currentJson = (current?.config_json ?? {}) as Record<string, unknown>;
      
      const nextConfigJson: DealCardPublishedConfigJson = {
        ...currentJson,
        meta: { schemaVersion: 2, ...((currentJson.meta as Record<string, unknown>) ?? {}) },
        data_elements: slice === "data_elements" ? sliceData : ((currentJson.data_elements ?? {}) as Record<string, unknown>),
        components: slice === "components" ? sliceData : ((currentJson.components ?? {}) as Record<string, unknown>),
      };
      
      const result = await publishConfig(selectedEnvironment, dealType, nextConfigJson, {
        expectedActiveVersionId: current?.active_version_id,
      });
      
      if (result) {
        console.log("[CR412] Published to", selectedEnvironment, ":", { slice, version: result.active_version_id });
        setPublishGeneration(g => g + 1);
        return true;
      }
      console.warn("[CR412] publishConfig returned null — check publishedError state");
      return false;
    } catch (err) {
      console.error("[CR412] Publish error:", err);
      return false;
    }
  }, [fetchPublishedConfig, publishConfig, selectedEnvironment]);

  // ================================================================
  // RENDER
  // ================================================================

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  // CR339: Split save handlers
  const saveDataElements = async () => {
    setSaving(true);
    isSavingRef.current = true; // CR342: Block draft persistence during save
    const trace: SaveTrace = {
      action: "update_template",
      dealTypeKey: selectedType,
      variantKey: "standard_v2",
      payloadTopKeys: [],
      status: "pending",
      timestamp: new Date().toISOString(),
    };
    setLastSaveTrace(trace);
    
    try {
      const { ribbon, debug_strip, merchant_category_chips, confidence_indicator, claim_button, ...coreSections } = draftSections;
      
      const templateJson: ExtendedCardTemplateJson = {
        sections: coreSections as CardTemplateSections,
        labels: savedTemplate?.labels ?? {}, // Use saved labels (owned by Components)
        header: savedTemplate?.header ?? DEFAULT_HEADER_COMPOSITION, // Use saved header
        extended_sections: { ribbon, debug_strip, merchant_category_chips, confidence_indicator, claim_button },
        styling: savedTemplate?.styling ?? DEFAULT_CARD_STYLING, // Use saved styling
        section_order: draftSectionOrder,
        theme_preset: savedTemplate?.themePreset ?? "light",
        surfaceOverrides: {
          primary: { 
            blockToggles: draftSurfaceBlockToggles.primary,
            blockOrder: draftSurfaceBlockOrder.primary.length > 0 ? draftSurfaceBlockOrder.primary : undefined,
          },
          details: { 
            blockToggles: draftSurfaceBlockToggles.details,
            blockOrder: draftSurfaceBlockOrder.details.length > 0 ? draftSurfaceBlockOrder.details : undefined,
          },
        },
        // CR339: promo_badges + details_content are Data Elements owned
        promo_badges: draftPromoBadges,
        details_content: draftDetailsContent,
      };
      
      trace.payloadTopKeys = Object.keys(templateJson);
      
      const { data: respData, error: tplError } = await supabase.functions.invoke("command-center-deal-cards", {
        method: "POST",
        body: { _action: "update_template", deal_type_key: selectedType, template_json: templateJson },
      });
      if (tplError) throw new Error(`Template save failed: ${tplError.message}`);
      
      const successTrace: SaveTrace = {
        ...trace,
        status: "success",
        responseSnippet: JSON.stringify(respData ?? {}).slice(0, 200),
      };
      setLastSaveTrace(successTrace);
      console.log("[CR340] saveDataElements trace:", successTrace);
      
      setLastSavedAt(new Date().toISOString());
      discardDraft(selectedType);
      restoredTypesRef.current.delete(selectedType);
      // CR422: Invalidate spec cache so Deal Card View picks up new data elements
      clearSpecCacheForType(selectedType);
      await fetchData();
      await refetchProvider();
      onRefresh?.();
      
      // CR412: Publish data_elements slice to CR412 tables
      const dealTypeForPublish = selectedType; // capture before any async gap
      const published = await publishToCR412(dealTypeForPublish, "data_elements", templateJson as unknown as Record<string, unknown>);
      if (published) {
        toast.success(`Data elements saved & published for ${dealTypeForPublish}`);
      } else {
        toast.error(`Data elements saved, but publish to ${selectedEnvironment} failed. Use "Retry publish" in Deal Card View.`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      const errorTrace: SaveTrace = {
        ...trace,
        status: "error",
        errorMessage: msg,
      };
      setLastSaveTrace(errorTrace);
      console.error("[CR340] saveDataElements error trace:", errorTrace);
      toast.error(`Save failed: ${msg}. Draft preserved.`);
    } finally {
      isSavingRef.current = false; // CR342: Re-enable draft persistence
      setSaving(false);
    }
  };
  
  const saveComponents = async () => {
    setSaving(true);
    isSavingRef.current = true; // CR342: Block draft persistence during save
    const trace: SaveTrace = {
      action: "update_components",
      dealTypeKey: selectedType,
      variantKey: "standard_v2",
      payloadTopKeys: [],
      status: "pending",
      timestamp: new Date().toISOString(),
    };
    setLastSaveTrace(trace);
    
    try {
      // Save header/labels/styling/themePreset to template store (components-owned)
      const { ribbon, debug_strip, merchant_category_chips, confidence_indicator, claim_button, ...coreSections } = draftSections;
      
      const templateJson: ExtendedCardTemplateJson = {
        sections: coreSections as CardTemplateSections,
        labels: draftLabels, // Components owns labels
        header: draftHeader, // Components owns header
        extended_sections: { ribbon, debug_strip, merchant_category_chips, confidence_indicator, claim_button },
        styling: draftStyling, // Components owns styling
        section_order: draftSectionOrder,
        theme_preset: themePreset, // Components owns themePreset
        surfaceOverrides: {
          primary: { 
            blockToggles: draftSurfaceBlockToggles.primary,
            blockOrder: draftSurfaceBlockOrder.primary.length > 0 ? draftSurfaceBlockOrder.primary : undefined,
          },
          details: { 
            blockToggles: draftSurfaceBlockToggles.details,
            blockOrder: draftSurfaceBlockOrder.details.length > 0 ? draftSurfaceBlockOrder.details : undefined,
          },
        },
        // CR339: Preserve Data Elements owned fields from saved state
        promo_badges: savedTemplate?.promoBadges ?? draftPromoBadges,
        details_content: savedTemplate?.detailsContent ?? draftDetailsContent,
      };
      
      trace.payloadTopKeys = ["template_json", "registry", "components_json"];
      
      const { error: tplError } = await supabase.functions.invoke("command-center-deal-cards", {
        method: "POST",
        body: { _action: "update_template", deal_type_key: selectedType, template_json: templateJson },
      });
      if (tplError) throw new Error(`Template save failed: ${tplError.message}`);
      
      // Save ribbon to registry
      const { error: regError } = await supabase.functions.invoke("command-center-deal-cards", {
        method: "POST",
        body: {
          _action: "update_registry",
          deal_type_key: selectedType,
          ribbon_text: draftRibbon.ribbon_text,
          ribbon_theme: draftRibbon.ribbon_theme,
          is_enabled: draftRibbon.is_enabled,
        },
      });
      if (regError) throw new Error(`Registry save failed: ${regError.message}`);
      
      // Save components config
      const componentsPayload = {
        ribbon: draftRibbon,
        components: draftComponents,
      };
      const { data: respData, error: compError } = await supabase.functions.invoke("command-center-deal-cards", {
        method: "POST",
        body: {
          _action: "update_components",
          deal_type_key: selectedType,
          variant_key: "standard_v2",
          components_json: componentsPayload,
        },
      });
      if (compError) throw new Error(`Components save failed: ${compError.message}`);
      
      const successTrace: SaveTrace = {
        ...trace,
        status: "success",
        responseSnippet: JSON.stringify(respData ?? {}).slice(0, 200),
      };
      setLastSaveTrace(successTrace);
      console.log("[CR340] saveComponents trace:", successTrace);
      
      setLastSavedAt(new Date().toISOString());
      discardDraft(selectedType);
      restoredTypesRef.current.delete(selectedType);
      
      // CR419: Invalidate spec cache so Deal Card View picks up new components
      clearSpecCacheForType(selectedType);
      
      await fetchData();
      await refetchProvider();
      onRefresh?.();
      
      // CR412: Publish components slice to CR412 tables
      // Use savedComponentsData directly (flat, no double-nesting)
      const dealTypeForPublish = selectedType; // capture before any async gap
      const componentsSlice: Record<string, unknown> = {
        ...(savedComponentsData ?? {}),
        // Override with just-saved values
        ribbon: draftRibbon,
        components: draftComponents,
      };
      const published = await publishToCR412(dealTypeForPublish, "components", componentsSlice);
      if (published) {
        toast.success(`Components saved & published for ${dealTypeForPublish}`);
      } else {
        toast.error(`Components saved, but publish to ${selectedEnvironment} failed. Use "Retry publish" in Deal Card View.`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      const errorTrace: SaveTrace = {
        ...trace,
        status: "error",
        errorMessage: msg,
      };
      setLastSaveTrace(errorTrace);
      console.error("[CR340] saveComponents error trace:", errorTrace);
      toast.error(`Save failed: ${msg}. Draft preserved.`);
    } finally {
      isSavingRef.current = false; // CR342: Re-enable draft persistence
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* CR412: Deal Cards sub-tabs: Deal Card View | Configure Data Elements | Components */}
      <Tabs value={topTab} onValueChange={(v) => setTopTab(v as "view" | "data" | "components")} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="view" className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Deal Card View
          </TabsTrigger>
          <TabsTrigger value="data" className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            Configure Data Elements
            {hasUnsavedDataChanges && (
              <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-[10px]">!</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="components" className="flex items-center gap-2">
            <Puzzle className="h-4 w-4" />
            Components
            {hasUnsavedComponentChanges && (
              <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-[10px]">!</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ============================================================ */}
        {/* TAB: Deal Card View (CR412 Phase 1.3A) */}
        {/* ============================================================ */}
        <TabsContent value="view" className="space-y-4">
          <DealCardViewTab
            selectedType={selectedType}
            onSelectedTypeChange={setSelectedType}
            selectedEnvironment={selectedEnvironment}
            onSelectedEnvironmentChange={setSelectedEnvironment}
            onNavigateToDataElements={() => setTopTab("data")}
            onNavigateToComponents={() => setTopTab("components")}
            published={publishedConfig}
            publishedLoading={publishedLoading}
            publishedError={publishedError}
            onRefreshPublished={() => fetchPublishedConfig(selectedEnvironment, selectedType)}
            onSeedPublish={async () => {
              // CR412: Seed publish — use exact saved models (no double-nesting)
              const templateJson = templateEntry?.template_json as ExtendedCardTemplateJson | undefined;
              const dataElements: Record<string, unknown> = templateJson ? {
                sections: templateJson.sections,
                labels: templateJson.labels,
                header: templateJson.header ?? savedTemplate?.header,
                styling: templateJson.styling ?? savedTemplate?.styling,
                extended_sections: templateJson.extended_sections,
                section_order: templateJson.section_order ?? savedTemplate?.sectionOrder,
                theme_preset: templateJson.theme_preset ?? savedTemplate?.themePreset,
                surfaceOverrides: templateJson.surfaceOverrides ?? {
                  primary: { blockToggles: savedTemplate?.surfaceBlockToggles.primary },
                  details: { blockToggles: savedTemplate?.surfaceBlockToggles.details },
                },
                promo_badges: templateJson.promo_badges ?? savedTemplate?.promoBadges,
                details_content: templateJson.details_content ?? savedTemplate?.detailsContent,
              } : {};
              // Components: use exact savedComponentsData (flat — ribbon + components at top level)
              const components: Record<string, unknown> = savedComponentsData ?? {};
              const configJson: DealCardPublishedConfigJson = {
                meta: { schemaVersion: 2 },
                data_elements: dataElements,
                components,
              };
              const result = await publishConfig(selectedEnvironment, selectedType, configJson);
              if (result) {
                toast.success(`Seeded ${selectedEnvironment} config for ${selectedType}`);
                setPublishGeneration(g => g + 1);
              } else {
                toast.error("Failed to seed publish");
              }
            }}
          />
        </TabsContent>

        {/* ============================================================ */}
        {/* TAB: Configure Data Elements (CR339) */}
        {/* ============================================================ */}
        <TabsContent value="data" className="space-y-4">
          <ConfigureDataElementsCore
            registry={registry}
            templates={templates}
            selectedType={selectedType}
            onTypeChange={setSelectedType}
            draftSections={draftSections}
            draftSurfaceBlockToggles={draftSurfaceBlockToggles}
            draftSurfaceBlockOrder={draftSurfaceBlockOrder}
            draftSectionOrder={draftSectionOrder}
            draftPromoBadges={draftPromoBadges}
            draftDetailsContent={draftDetailsContent}
            onSectionToggle={handleSectionToggle}
            onSurfaceBlockToggle={handleSurfaceBlockToggle}
            onSurfaceBlockOrderChange={handleSurfaceBlockOrderChange}
            onSectionOrderChange={handleSectionOrderChange}
            onStylingChange={handleStylingChange}
            onPromoBadgesChange={handlePromoBadgesChange}
            onDetailsContentChange={handleDetailsContentChange}
            savedRibbon={savedComponentsForPreview.ribbon}
            savedHeader={savedComponentsForPreview.header}
            savedLabels={savedComponentsForPreview.labels}
            savedStyling={savedComponentsForPreview.styling}
            savedThemePreset={savedComponentsForPreview.themePreset}
            savedComponents={savedComponentsForPreview.components}
            onSave={saveDataElements}
            onDiscard={discardChanges}
            hasUnsavedChanges={hasUnsavedDataChanges}
            saving={saving}
            loading={loading}
            publishEnvironment={selectedEnvironment}
          />
        </TabsContent>

        {/* ============================================================ */}
        {/* TAB: Components (CR339: Independent save/dirty) */}
        {/* ============================================================ */}
        <TabsContent value="components" className="space-y-4">
          <ComponentsTabView
            registry={registry}
            templates={templates}
            selectedType={selectedType}
            onSelectedTypeChange={setSelectedType}
            draftComponents={draftComponents}
            draftStyling={draftStyling}
            draftSections={draftSections}
            draftSurfaceBlockToggles={draftSurfaceBlockToggles}
            draftRibbon={draftRibbon}
            draftHeader={draftHeader}
            draftLabels={draftLabels}
            draftSectionOrder={draftSectionOrder}
            draftSurfaceBlockOrder={draftSurfaceBlockOrder}
            themePreset={themePreset}
            savedComponents={savedComponentsData?.components}
            savedStyling={savedTemplate?.styling}
            savedRibbon={savedComponentsData?.ribbon}
            lastSaveTrace={lastSaveTrace}
            onComponentsChange={handleComponentsChange}
            onStylingChange={handleStylingChange}
            onSave={saveComponents}
            onDiscard={discardChanges}
            hasUnsavedChanges={hasUnsavedComponentChanges}
            saving={saving}
            publishEnvironment={selectedEnvironment}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
